import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, Link2, PlugZap, Unplug, XCircle } from 'lucide-react';
import type { DriveSettings as DriveFolders } from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { SkeletonLines } from '@/components/ui/skeleton';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from '@/components/ui/toaster';
import { AdminNote, SettingsCard, SettingsSection } from '@/pages/settings/SettingsSection';
import { useSettingsQuery, useUpdateSettings } from '@/pages/settings/useSettings';

/** `GET /drive/status`.lastLinkCheck — the last JobRun of the link check. */
export interface LinkCheckResult {
  id?: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  ok?: boolean | null;
  summary?: unknown;
}

/** `POST /drive/check-links` — the counters of the run that just finished. */
export interface LinkCheckRun {
  checked: number;
  missing: number;
  restored: number;
  errors: number;
  jobRunId: string;
}

/** Folder ids the API resolved (settings, else env, else the fake defaults); `null` when unset. */
export interface DriveStatusFolders {
  root: string | null;
  openJds: string | null;
  opportunities: string | null;
  candidates: string | null;
}

export interface DriveStatus {
  mode: 'oauth' | 'service_account' | string;
  fake?: boolean;
  connected: boolean;
  accountEmail?: string | null;
  connectedAt?: string | null;
  connectedBy?: string | null;
  folders?: Partial<DriveStatusFolders> | null;
  foldersConfigured?: boolean;
  lastLinkCheck?: LinkCheckResult | null;
  brokenFiles?: number;
}

function linkCheckResultFromRun(run: LinkCheckRun): LinkCheckResult {
  return {
    id: run.jobRunId,
    ok: run.errors === 0,
    finishedAt: new Date().toISOString(),
    summary: { checked: run.checked, missing: run.missing, restored: run.restored, errors: run.errors },
  };
}

const STATUS_KEY = ['drive', 'status'] as const;

const MODE_LABELS: Record<string, string> = {
  oauth: 'Google account (OAuth)',
  service_account: 'Service account',
  fake: 'In-memory (development)',
};

const FOLDER_FIELDS: Array<{ key: keyof DriveFolders; label: string; help: string }> = [
  {
    key: 'rootFolderId',
    label: 'Root folder',
    help: 'The data-claw recruiting folder everything lives under.',
  },
  { key: 'openJdsFolderId', label: 'Open JDs folder', help: 'Where the open-JD PDF copies are published.' },
  { key: 'opportunitiesFolderId', label: 'Opportunities folder', help: 'One sub-folder per position.' },
  { key: 'candidatesFolderId', label: 'Candidates folder', help: 'One sub-folder per candidate.' },
  { key: 'templatesFolderId', label: 'Templates folder', help: 'DOCX templates for JDs and profiles.' },
];

const folderSchema = z.object({
  rootFolderId: z.string().trim(),
  openJdsFolderId: z.string().trim(),
  opportunitiesFolderId: z.string().trim(),
  candidatesFolderId: z.string().trim(),
  templatesFolderId: z.string().trim(),
});
type FolderValues = z.infer<typeof folderSchema>;

const EMPTY_FOLDERS: FolderValues = {
  rootFolderId: '',
  openJdsFolderId: '',
  opportunitiesFolderId: '',
  candidatesFolderId: '',
  templatesFolderId: '',
};

/** Google Drive (spec §6.3): connection, folder IDs and the link check. */
export function DriveSettings() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Back from Google's consent screen: `/settings/drive?connected=1` → toast once and drop the param.
  const justConnected = searchParams.get('connected') === '1';
  React.useEffect(() => {
    if (!justConnected) return;
    toast.success('Google Drive connected');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('connected');
        return next;
      },
      { replace: true },
    );
    void queryClient.invalidateQueries({ queryKey: STATUS_KEY });
  }, [justConnected, setSearchParams, queryClient]);

  const status = useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => api.get<DriveStatus>('/drive/status'),
  });

  const connect = useMutation({
    mutationFn: () => api.get<{ url: string }>('/drive/connect'),
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const disconnect = useMutation({
    mutationFn: () => api.post('/drive/disconnect'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      toast.success('Google Drive disconnected');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const checkLinks = useMutation({
    mutationFn: () => api.post<LinkCheckRun | undefined>('/drive/check-links'),
    onSuccess: async (run) => {
      if (run) {
        queryClient.setQueryData<DriveStatus>(STATUS_KEY, (old) =>
          old ? { ...old, lastLinkCheck: linkCheckResultFromRun(run) } : old,
        );
      }
      await queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(
        run
          ? `Link check finished: ${run.checked} checked, ${run.missing} missing, ${run.restored} restored${run.errors ? `, ${run.errors} errors` : ''}`
          : 'Link check finished',
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const s = status.data;
  const lastCheck = s?.lastLinkCheck ?? null;
  const unavailable = status.isError && isApiError(status.error) && status.error.status === 404;

  return (
    <SettingsSection
      title="Google Drive"
      description="Documents are stored in Drive: one folder per position and per candidate, plus the public open-JDs folder."
    >
      {isAdmin ? null : <AdminNote what="the Drive connection" />}

      <SettingsCard title="Connection">
        {status.isLoading ? (
          <SkeletonLines lines={3} />
        ) : status.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {unavailable
              ? 'The Drive status endpoint is not available on this API yet.'
              : `Drive status could not be loaded: ${status.error.message}`}
          </p>
        ) : s ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                {s.connected ? (
                  <Badge className="border-transparent bg-emerald-100 text-emerald-800 shadow-none dark:bg-emerald-950 dark:text-emerald-300">
                    <CheckCircle2 className="mr-1 h-3 w-3" aria-hidden="true" /> Connected
                  </Badge>
                ) : (
                  <Badge className="border-transparent bg-amber-100 text-amber-800 shadow-none dark:bg-amber-950 dark:text-amber-300">
                    <XCircle className="mr-1 h-3 w-3" aria-hidden="true" /> Not connected
                  </Badge>
                )}
              </dd>
              <dt className="text-muted-foreground">Mode</dt>
              <dd>{MODE_LABELS[s.mode] ?? s.mode}</dd>
              {s.accountEmail ? (
                <>
                  <dt className="text-muted-foreground">Account</dt>
                  <dd>{s.accountEmail}</dd>
                </>
              ) : null}
              {s.connectedAt ? (
                <>
                  <dt className="text-muted-foreground">Since</dt>
                  <dd className="tabular-nums">{formatDateTime(s.connectedAt)}</dd>
                </>
              ) : null}
            </dl>
            {isAdmin ? (
              <div className="flex flex-wrap gap-2">
                {s.mode === 'oauth' || !s.connected ? (
                  <Button
                    onClick={() => connect.mutate()}
                    loading={connect.isPending}
                    disabled={s.mode === 'fake'}
                  >
                    <PlugZap aria-hidden="true" /> {s.connected ? 'Reconnect' : 'Connect Google Drive'}
                  </Button>
                ) : null}
                {s.connected ? (
                  <Button
                    variant="outline"
                    loading={disconnect.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          'Disconnect Google Drive? Documents cannot be generated until it is connected again.',
                        )
                      )
                        disconnect.mutate();
                    }}
                  >
                    <Unplug aria-hidden="true" /> Disconnect
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </SettingsCard>

      <FoldersCard readOnly={!isAdmin} statusFolders={s?.folders ?? null} />

      <SettingsCard
        title="Link check"
        description="Verifies that every file linked to a record still exists in Drive. Broken links show up on the dashboard."
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="text-sm">
            {lastCheck ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                <dt className="text-muted-foreground">Result</dt>
                <dd>
                  {lastCheck.ok === false ? (
                    <Badge className="border-transparent bg-rose-100 text-rose-800 shadow-none dark:bg-rose-950 dark:text-rose-300">
                      Problems found
                    </Badge>
                  ) : (
                    <Badge className="border-transparent bg-emerald-100 text-emerald-800 shadow-none dark:bg-emerald-950 dark:text-emerald-300">
                      OK
                    </Badge>
                  )}
                </dd>
                {lastCheck.startedAt ? (
                  <>
                    <dt className="text-muted-foreground">Started</dt>
                    <dd className="tabular-nums">{formatDateTime(lastCheck.startedAt)}</dd>
                  </>
                ) : null}
                {lastCheck.finishedAt ? (
                  <>
                    <dt className="text-muted-foreground">Finished</dt>
                    <dd className="tabular-nums">{formatDateTime(lastCheck.finishedAt)}</dd>
                  </>
                ) : null}
                {lastCheck.summary !== undefined && lastCheck.summary !== null ? (
                  <>
                    <dt className="text-muted-foreground">Summary</dt>
                    <dd>
                      <Summary value={lastCheck.summary} />
                    </dd>
                  </>
                ) : null}
              </dl>
            ) : (
              <p className="text-muted-foreground">No link check has run yet.</p>
            )}
          </div>
          {isAdmin ? (
            <Button variant="outline" onClick={() => checkLinks.mutate()} loading={checkLinks.isPending}>
              <Link2 aria-hidden="true" /> Run link check now
            </Button>
          ) : null}
        </div>
      </SettingsCard>
    </SettingsSection>
  );
}

function Summary({ value }: { value: unknown }) {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return <span>{String(value)}</span>;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>);
    return (
      <ul className="space-y-0.5">
        {entries.map(([k, v]) => (
          <li key={k} className="tabular-nums">
            <span className="text-muted-foreground">{k}:</span>{' '}
            {typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}
          </li>
        ))}
      </ul>
    );
  }
  return <code className="text-xs">{JSON.stringify(value)}</code>;
}

function FoldersCard({
  readOnly,
  statusFolders,
}: {
  readOnly: boolean;
  statusFolders: Partial<DriveStatusFolders> | null;
}) {
  const settings = useSettingsQuery();
  const update = useUpdateSettings('Drive folders saved');
  const values = React.useMemo<FolderValues | undefined>(() => {
    if (!settings.data) return undefined;
    const d = settings.data.drive;
    // Settings win; the Drive status pre-fills the folders resolved from the environment.
    return {
      rootFolderId: d.rootFolderId || statusFolders?.root || '',
      openJdsFolderId: d.openJdsFolderId || statusFolders?.openJds || '',
      opportunitiesFolderId: d.opportunitiesFolderId || statusFolders?.opportunities || '',
      candidatesFolderId: d.candidatesFolderId || statusFolders?.candidates || '',
      templatesFolderId: d.templatesFolderId,
    };
  }, [settings.data, statusFolders]);

  const form = useForm<FolderValues>({
    resolver: zodResolver(folderSchema),
    defaultValues: EMPTY_FOLDERS,
    values,
    resetOptions: { keepDirtyValues: true },
  });

  const onSubmit = async (v: FolderValues) => {
    const saved = await update.mutateAsync({ drive: v });
    form.reset(saved.drive);
  };

  return (
    <SettingsCard
      title="Folder IDs"
      description="The Drive folder IDs from the folder URLs (the part after /folders/)."
    >
      {settings.isLoading ? (
        <SkeletonLines lines={5} />
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <fieldset disabled={readOnly} className="grid gap-4 sm:grid-cols-2">
              {FOLDER_FIELDS.map((f) => (
                <FormField
                  key={f.key}
                  control={form.control}
                  name={f.key}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{f.label}</FormLabel>
                      <FormControl>
                        <Input className="font-mono text-xs" spellCheck={false} {...field} />
                      </FormControl>
                      <FormDescription>{f.help}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </fieldset>
            {readOnly ? null : (
              <div className="flex gap-2">
                <Button type="submit" loading={update.isPending} disabled={!form.formState.isDirty}>
                  Save folders
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => form.reset()}
                  disabled={!form.formState.isDirty}
                >
                  Discard
                </Button>
              </div>
            )}
          </form>
        </Form>
      )}
    </SettingsCard>
  );
}
